import React, { useState } from "react";
import { getCollectiveMetrics } from "../../../Rest_api/RestApi";
import { ExchangeMetrics } from "../../../models/metrics";

// Define your primary and secondary colors.
export const PrimaryColor = "#ff7c04";
export const SecondaryColor = "#34656D";

const ExchangeMetricsForm: React.FC = () => {
  // Form state.
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  // Set default values as IEX and DAM.
  const [exchangeName, setExchangeName] = useState("IEX");
  const [productName, setProductName] = useState("DAM");
  const [blocksInput, setBlocksInput] = useState("");

  // API result state.
  const [metrics, setMetrics] = useState<ExchangeMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Form submission handler.
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMetrics(null);
    try {
      const parsedBlocks = blocksInput
        .split(",")
        .map((block) => block.trim())
        .filter((block) => block.length > 0)
        .map((block) => Number(block))
        .filter((block) => Number.isFinite(block));

      const response = await getCollectiveMetrics({
        ranges: [{
          startDate,
          endDate: endDate || undefined,
          exchangeName: exchangeName || undefined,
          productName: productName || undefined,
          blocks: parsedBlocks.length > 0 ? parsedBlocks : undefined,
        }],
        aggregated: true,
      });
      // When aggregated=true, API returns a single ExchangeMetrics object
      setMetrics(Array.isArray(response) ? response[0] : response);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch metrics.");
    }
    setLoading(false);
  };

  // Clear form handler
  const clearForm = () => {
    setStartDate("");
    setEndDate("");
    setExchangeName("IEX"); // Reset to default
    setProductName("DAM"); // Reset to default
    setBlocksInput("");
    // Optionally clear results too
    setMetrics(null);
    setError(null);
  };

  // Utility function to format metric values to 3 decimal places if they are numbers.
  const formatMetricValue = (value: number | undefined): string => {
    return typeof value === "number" ? value.toFixed(3) : "N/A";
  };

  return (
    <div className="w-full m-5">
      <form
        onSubmit={handleSubmit}
        className="space-y-6 bg-white rounded-2xl p-6"
      >
        <h1 className="text-[1.2rem] text-[#A3AED0] w-full border-b pb-5">
          Collective Price Metrics
        </h1>
        {/* Row 1: Start Date & End Date */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label
              className="block font-medium text-sm mb-3"
              style={{ color: SecondaryColor }}
            >
              Start Date (YYYY-MM-DD)*
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="p-2 border rounded w-full focus:outline-none focus:ring-2"
              required
            />
          </div>
          <div>
            <label
              className="block font-medium text-sm mb-3"
              style={{ color: SecondaryColor }}
            >
              End Date (YYYY-MM-DD)
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="p-2 border rounded w-full focus:outline-none focus:ring-2"
            />
          </div>
        </div>

        {/* Row 2: Exchange Name & Product Name */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label
              className="block font-medium text-sm mb-3"
              style={{ color: SecondaryColor }}
            >
              Exchange Name
            </label>
            <select
              value={exchangeName}
              onChange={(e) => setExchangeName(e.target.value)}
              className="p-2 border rounded w-full focus:outline-none focus:ring-2"
            >
              <option value="IEX">IEX</option>
              <option value="RTM">RTM</option>
              <option value="PXIL">PXIL</option>
            </select>
          </div>
          <div>
            <label
              className="block font-medium text-sm mb-3"
              style={{ color: SecondaryColor }}
            >
              Product Name
            </label>
            <select
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              className="p-2 border rounded w-full focus:outline-none focus:ring-2"
            >
              <option value="DAM">DAM</option>
              <option value="RTM">RTM</option>
              <option value="GDAM">GDAM</option>
              <option value="HPDAM">HPDAM</option>
            </select>
          </div>
        </div>

        {/* Row 3: Explicit Blocks */}
        <div>
          <label
            className="block font-medium text-sm mb-3"
            style={{ color: SecondaryColor }}
          >
            Blocks (comma-separated)
          </label>
          <input
            type="text"
            value={blocksInput}
            onChange={(e) => setBlocksInput(e.target.value)}
            className="p-2 border rounded w-full focus:outline-none focus:ring-2"
            placeholder="Example: 1, 5, 20, 33"
          />
        </div>

        {/* Submit and Clear Buttons */}
        <div className="flex justify-center gap-4">
          <button
            type="button"
            className="px-6 py-2 rounded-lg text-zinc-500 font-medium border border-gray-300 hover:bg-gray-100 transition duration-200"
            onClick={clearForm}
          >
            Clear Form
          </button>

          <button
            type="submit"
            className="px-6 py-2 rounded-lg text-white font-medium transition duration-200"
            style={{ backgroundColor: PrimaryColor }}
            disabled={loading}
          >
            {loading ? "Loading..." : "Get Metrics"}
          </button>
        </div>
      </form>

      {error && <div className="mt-4 text-red-600 text-center">{error}</div>}

      {metrics && (
        <div className="mt-5 bg-white rounded-2xl p-4">
          <h2 className="text-xl font-medium mb-4 text-[#A3AED0] border-b pb-2">
            Results
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Card 1: Weighted Avg Price */}
            <div className="flex flex-col items-center bg-white p-4 rounded-lg drop-shadow-lg transform hover:scale-105 transition duration-300 ">
              <div className="text-[#A3AED0] text-2xl mb-2">
                <i className="fas fa-chart-bar"></i>{" "}
                {/* Replace with an appropriate icon */}
              </div>
              <h3 className="text-sm  text-[#A3AED0] mb-1">
                Weighted Avg Price
              </h3>
              <p className="text-xl font-bold text-[#2B3674]">
                {formatMetricValue(metrics.weighted_avg_price.value)}{" "}
                <span className="text-sm text-[#A3AED0]">
                  {metrics.weighted_avg_price.unit}
                </span>
              </p>
            </div>

            {/* Card 2: Avg Volume */}
            <div className="flex flex-col items-center bg-white p-4 rounded-lg drop-shadow-lg transform hover:scale-105 transition duration-300 ">
              <div className="text-[#F4A261] text-2xl mb-2">
                <i className="fas fa-chart-line"></i>{" "}
                {/* Replace with an appropriate icon */}
              </div>
              <h3 className="text-sm  text-[#A3AED0] mb-1">Avg Volume</h3>
              <p className="text-xl font-bold text-[#2B3674]">
                {formatMetricValue(metrics.avg_volume.value)}{" "}
                <span className="text-sm text-[#A3AED0]">
                  {metrics.avg_volume.unit}
                </span>
              </p>
            </div>

            {/* Card 3: Total Volume */}
            <div className="flex flex-col items-center bg-white p-4 rounded-lg drop-shadow-lg transform hover:scale-105 transition duration-300 ">
              <div className="text-[#2A9D8F] text-2xl mb-2">
                <i className="fas fa-database"></i>{" "}
                {/* Replace with an appropriate icon */}
              </div>
              <h3 className="text-sm text-[#A3AED0] mb-1">Total Volume</h3>
              <p className="text-xl font-bold text-[#2B3674]">
                {formatMetricValue(metrics.total_volume_mu.value)}{" "}
                <span className="text-sm text-[#A3AED0]">
                  {metrics.total_volume_mu.unit}
                </span>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExchangeMetricsForm;
